import React from 'react';
import './GeneratorSkeleton.css';

/* Mirrors the real generator: five gear slots either side of the character,
   six trophies under them, then the two-column stat sheet. Keep these in step
   with BuildPanel.js and the stat groups BuildStats renders — a placeholder
   that resolves into a different shape is the thing this is meant to avoid. */
const GEAR_SLOTS_PER_COLUMN = 5;
const TROPHY_SLOTS = 6;
const HEADER_ACTIONS = 5;
const STAT_GROUP_ROWS = [2, 6, 3, 5, 5];

const blocks = (count, className) => Array.from({ length: count }, (_, i) => (
    <div key={i} className={`generator-skeleton-block ${className}`} />
));

/**
 * Placeholder shown while the generator bootstraps.
 *
 * A saved build is sixteen item *ids*; the item catalog resolves them. Until it
 * arrives there is nothing truthful to draw — the grid would be empty and the
 * stat sheet would be missing every item and set bonus, which reads as a lost
 * build rather than a loading one. See `useGeneratorState.isBootstrapping`.
 *
 * Usually invisible: the catalog is immutable-cached for a year, so only a
 * first visit waits long enough to see this.
 */
const GeneratorSkeleton = () => (
    <div className="generator-skeleton" aria-busy="true" aria-live="polite">
        <div className="generator-skeleton-header">
            <div className="generator-skeleton-block generator-skeleton-title" />
            <div className="generator-skeleton-header-actions">
                {blocks(HEADER_ACTIONS, 'generator-skeleton-action')}
            </div>
        </div>

        <div className="generator-skeleton-grid">
            <div className="generator-skeleton-build panel">
                <div className="generator-skeleton-build-header">
                    <div className="generator-skeleton-block generator-skeleton-class" />
                    <div className="generator-skeleton-block generator-skeleton-level" />
                </div>
                <div className="generator-skeleton-slots">
                    <div className="generator-skeleton-gear">
                        {blocks(GEAR_SLOTS_PER_COLUMN, 'generator-skeleton-slot')}
                    </div>
                    <div className="generator-skeleton-block generator-skeleton-figure" />
                    <div className="generator-skeleton-gear">
                        {blocks(GEAR_SLOTS_PER_COLUMN, 'generator-skeleton-slot')}
                    </div>
                </div>
                <div className="generator-skeleton-trophies">
                    {blocks(TROPHY_SLOTS, 'generator-skeleton-trophy')}
                </div>
                <div className="generator-skeleton-block generator-skeleton-sets" />
            </div>

            <div className="generator-skeleton-data panel">
                <div className="generator-skeleton-tabs">
                    {blocks(2, 'generator-skeleton-tab')}
                </div>
                <div className="generator-skeleton-stats">
                    <div className="generator-skeleton-stats-content">
                        {STAT_GROUP_ROWS.map((rows, group) => (
                            <React.Fragment key={group}>
                                {group > 0 && <div className="generator-skeleton-stat-divider" />}
                                <div className="generator-skeleton-stat-column">
                                    {blocks(rows, 'generator-skeleton-stat-row')}
                                </div>
                                <div className="generator-skeleton-stat-column">
                                    {blocks(rows, 'generator-skeleton-stat-row')}
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
        </div>

        <div className="generator-skeleton-block generator-skeleton-bar" />
    </div>
);

export default GeneratorSkeleton;
